import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Warehouse as WarehouseIcon, X, AlertTriangle } from "lucide-react";

/**
 * Bulk-assign-warehouse modal (Sprint 4.7).
 *
 * Used from Inventory (mode="inventory") and Assets (mode="assets") pages
 * to retro-tag legacy batches / asset records onto a chosen warehouse.
 *
 * Backend endpoints:
 *   POST /api/inventory/batches/bulk-assign-warehouse
 *   POST /api/assets/bulk-assign-warehouse
 *
 * Scope picker:
 *   • only-missing   (default · safest — re-tags rows with no warehouse_id)
 *   • all-in-branch  (admin escape hatch — re-tags every row in the chosen branch)
 *
 * Both write a single audit-log entry under action="bulk_assign_inventory"
 * / "bulk_assign_assets".
 */
export function BulkAssignWarehouseModal({ mode = "inventory", defaultBranchId = "", onClose, onDone }) {
  const [branches, setBranches] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [coverage, setCoverage] = useState(null);
  const [branchId, setBranchId] = useState(defaultBranchId);
  const [targetWarehouseId, setTargetWarehouseId] = useState("");
  const [onlyMissing, setOnlyMissing] = useState(true);
  const [saving, setSaving] = useState(false);

  // Load branches + warehouses + coverage on mount and whenever branch changes
  useEffect(() => {
    api.get("/branches").then(r => setBranches(Array.isArray(r.data) ? r.data : []))
                        .catch(() => setBranches([]));
  }, []);

  useEffect(() => {
    const url = branchId ? `/warehouses/picker?branch_id=${branchId}` : "/warehouses/picker";
    api.get(url).then(r => {
      const arr = Array.isArray(r.data) ? r.data.filter(w => w.is_active !== false) : [];
      setWarehouses(arr);
      // If the previously-selected target is no longer valid (branch changed),
      // reset to the branch default OR the first warehouse in the branch.
      if (targetWarehouseId && !arr.find(w => w.id === targetWarehouseId)) {
        const dflt = arr.find(w => w.is_default) || arr[0];
        setTargetWarehouseId(dflt?.id || "");
      } else if (!targetWarehouseId && arr.length) {
        const dflt = arr.find(w => w.is_default) || arr[0];
        setTargetWarehouseId(dflt?.id || "");
      }
    }).catch(() => setWarehouses([]));
    // Coverage for this scope
    const cv = branchId ? `?branch_id=${branchId}` : "";
    api.get(`/inventory/warehouse-coverage${cv}`)
       .then(r => setCoverage(r.data))
       .catch(() => setCoverage(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branchId]);

  const sourceTotals = useMemo(() => {
    if (!coverage) return { total: 0, missing: 0 };
    return mode === "assets" ? coverage.assets : coverage.inventory;
  }, [coverage, mode]);

  const targetWh = warehouses.find(w => w.id === targetWarehouseId);

  async function submit() {
    if (!targetWarehouseId) {
      toast.error("Pick a target warehouse");
      return;
    }
    const scopeLabel = onlyMissing
      ? `only the ${sourceTotals.missing} record(s) without a warehouse`
      : `ALL ${sourceTotals.total} record(s) in the selected branch`;
    const ok = window.confirm(
      `Tag ${scopeLabel} to:\n` +
      `  ${targetWh?.warehouse_code} — ${targetWh?.warehouse_name}\n\n` +
      `This is audit-logged. Continue?`,
    );
    if (!ok) return;
    setSaving(true);
    try {
      const url = mode === "assets"
        ? "/assets/bulk-assign-warehouse"
        : "/inventory/batches/bulk-assign-warehouse";
      const { data } = await api.post(url, {
        target_warehouse_id: targetWarehouseId,
        branch_id: branchId || null,
        only_missing: onlyMissing,
      });
      toast.success(
        `${data.modified} ${mode === "assets" ? "asset(s)" : "batch(es)"} tagged to ${data.warehouse_name}`,
        { duration: 6000 },
      );
      if (onDone) onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Bulk-assign failed");
    } finally {
      setSaving(false);
    }
  }

  const label = mode === "assets" ? "Assets" : "Inventory Batches";

  return (
    <div className="fixed inset-0 z-[100] bg-slate-900/50 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-white w-full max-w-lg shadow-xl max-h-[92vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
        data-testid="bulk-assign-warehouse-modal"
      >
        <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white z-10">
          <div className="flex items-center gap-2">
            <WarehouseIcon size={14} className="text-[#F97316]" />
            <h3 className="font-display text-lg font-bold">Tag {label} to Warehouse</h3>
          </div>
          <button type="button" onClick={onClose}
                  className="text-slate-400 hover:text-slate-700 text-xl leading-none px-1"
                  data-testid="bulk-assign-warehouse-close">
            <X size={18} />
          </button>
        </div>

        <div className="p-5 space-y-4 text-sm">
          {/* Branch */}
          <label className="block">
            <span className="block text-[10px] uppercase tracking-[0.18em] font-bold text-slate-500 mb-1">Branch (optional)</span>
            <select data-testid="bulk-assign-branch"
              value={branchId}
              onChange={e => { setBranchId(e.target.value); setTargetWarehouseId(""); }}
              className="w-full border border-slate-300 px-3 py-2 text-sm">
              <option value="">All branches user can access</option>
              {branches.map(b => (
                <option key={b.id} value={b.id}>{b.branch_name}</option>
              ))}
            </select>
            <p className="text-[11px] text-slate-500 mt-1">
              The target warehouse list is filtered to this branch. The bulk update is also branch-scoped.
            </p>
          </label>

          {/* Target warehouse */}
          <label className="block">
            <span className="block text-[10px] uppercase tracking-[0.18em] font-bold text-slate-500 mb-1">
              Target Warehouse *
            </span>
            <select data-testid="bulk-assign-target-warehouse"
              value={targetWarehouseId}
              onChange={e => setTargetWarehouseId(e.target.value)}
              className="w-full border border-slate-300 px-3 py-2 text-sm">
              <option value="">— Select target warehouse —</option>
              {warehouses.map(w => (
                <option key={w.id} value={w.id}>
                  {w.warehouse_code} — {w.warehouse_name}{w.is_default ? " · default" : ""}
                </option>
              ))}
            </select>
            {warehouses.length === 0 && (
              <p className="text-[11px] text-rose-600 mt-1">
                No active warehouse available in this branch. Create one in Company Setup first.
              </p>
            )}
          </label>

          {/* Coverage tile */}
          <div className="bg-slate-50 border border-slate-200 p-3">
            <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-500 mb-1">
              Current coverage{branchId ? " (this branch)" : " (all your branches)"}
            </div>
            {coverage ? (
              <div className="text-xs text-slate-700 flex items-center gap-4 flex-wrap">
                <span>Total <strong className="tabular-nums" data-testid="bulk-assign-total">{sourceTotals.total}</strong></span>
                <span className="text-emerald-700">Tagged <strong className="tabular-nums">{sourceTotals.total - sourceTotals.missing}</strong></span>
                <span className="text-amber-700">Missing <strong className="tabular-nums" data-testid="bulk-assign-missing">{sourceTotals.missing}</strong></span>
              </div>
            ) : (
              <div className="text-[11px] text-slate-400">Loading coverage…</div>
            )}
          </div>

          {/* Scope */}
          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-500 mb-2">Scope</div>
            <label className="flex items-start gap-2 mb-2 cursor-pointer">
              <input type="radio" name="bulk-assign-scope"
                data-testid="bulk-assign-scope-missing"
                checked={onlyMissing}
                onChange={() => setOnlyMissing(true)}
                className="accent-[#F97316] mt-0.5" />
              <span className="text-xs">
                <strong className="block">Only records without a warehouse</strong>
                <span className="text-slate-500">Re-tag the <strong className="tabular-nums">{sourceTotals.missing}</strong> orphan record(s). Safest option — recommended for legacy data.</span>
              </span>
            </label>
            <label className="flex items-start gap-2 cursor-pointer">
              <input type="radio" name="bulk-assign-scope"
                data-testid="bulk-assign-scope-all"
                checked={!onlyMissing}
                onChange={() => setOnlyMissing(false)}
                className="accent-[#F97316] mt-0.5" />
              <span className="text-xs">
                <strong className="block">ALL records in the chosen branch</strong>
                <span className="text-slate-500">Overwrites existing warehouse tags. Use only when consolidating onto a single warehouse.</span>
              </span>
            </label>
            {!onlyMissing && (
              <div className="mt-2 flex items-center gap-2 text-[11px] text-rose-700 bg-rose-50 border border-rose-200 px-2.5 py-1.5"
                   data-testid="bulk-assign-overwrite-warning">
                <AlertTriangle size={12} />
                <span>This will overwrite warehouse tags on <strong className="tabular-nums">{sourceTotals.total}</strong> record(s). Irreversible from the UI.</span>
              </div>
            )}
          </div>
        </div>

        <div className="px-5 py-3 border-t border-slate-200 flex justify-end gap-2 sticky bottom-0 bg-white z-10">
          <button type="button" onClick={onClose}
            className="px-4 py-2 text-xs uppercase tracking-wider font-bold text-slate-600 hover:bg-slate-100">
            Cancel
          </button>
          <button type="button"
            data-testid="bulk-assign-submit-btn"
            disabled={saving || !targetWarehouseId || (onlyMissing && sourceTotals.missing === 0) || (!onlyMissing && sourceTotals.total === 0)}
            onClick={submit}
            className="bg-[#F97316] hover:bg-[#EA580C] disabled:opacity-50 text-white px-4 py-2 text-xs uppercase tracking-wider font-bold">
            {saving ? "Tagging…" : "Tag to Warehouse"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default BulkAssignWarehouseModal;
